// spec-8 — pure helpers for the engagement board (Live view): the "how long ago"
// relative-time label (ac-2 UI part), the client-side filter composition
// (cohort / hide-internal / global search — ac-6/ac-10 UI parts) and the
// Busiest⇄Most-recent client-side reorder (ac-7/ac-24). Kept pure + separate from
// the page so the ranking + composition are directly unit-testable with no refetch.
import type { ActivityRow, ActivityCohort } from "../api/types";

/** Compact "how long ago" label for an ISO instant ("2m ago", "3h ago", "5d
 *  ago"). `null` renders as an em dash. `now` is injectable for deterministic
 *  tests. */
export function relativeTime(iso: string | null, now: number = Date.now()): string {
  if (!iso) return "—";
  const diff = now - new Date(iso).getTime();
  if (Number.isNaN(diff)) return "—";
  if (diff < 0) return "now";
  const s = Math.floor(diff / 1000);
  if (s < 60) return "just now";
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 7) return `${d}d ago`;
  const w = Math.floor(d / 7);
  if (w < 5) return `${w}w ago`;
  const mo = Math.floor(d / 30);
  if (mo < 12) return `${mo}mo ago`;
  return `${Math.floor(d / 365)}y ago`;
}

export type SortOrder = "busiest" | "recent";

/** Reorder the ALREADY-LOADED rows client-side — no refetch (ac-7/ac-24).
 *  "busiest" = busyness desc; "recent" = lastAction.at desc (idle rows last). */
export function sortActivity(rows: ActivityRow[], order: SortOrder): ActivityRow[] {
  return [...rows].sort((a, b) => {
    if (order === "busiest") return b.busyness - a.busyness;
    const ta = a.lastAction ? Date.parse(a.lastAction.at) : -Infinity;
    const tb = b.lastAction ? Date.parse(b.lastAction.at) : -Infinity;
    return tb - ta;
  });
}

export interface ActivityFilters {
  search: string; // global: name / email / org / tags
  cohort: "all" | ActivityCohort;
  hideInternal: boolean;
}

// Hide-internal defaults ON (spec-4 parity) so the board shows real external signal.
export const EMPTY_ACTIVITY_FILTERS: ActivityFilters = {
  search: "",
  cohort: "all",
  hideInternal: true,
};

const orgString = (u: ActivityRow) => u.orgs.map((o) => o.orgName).join(" ");
const tagString = (u: ActivityRow) => u.tags.map((t) => `${t.scope}:${t.value}`).join(" ");

export function filterActivity(rows: ActivityRow[], f: ActivityFilters): ActivityRow[] {
  const search = f.search.trim().toLowerCase();
  return rows.filter((u) => {
    if (f.hideInternal && u.isInternal) return false;
    if (f.cohort !== "all" && u.cohort !== f.cohort) return false;
    if (search) {
      const hay = `${u.name ?? ""} ${u.email} ${orgString(u)} ${tagString(u)}`.toLowerCase();
      if (!hay.includes(search)) return false;
    }
    return true;
  });
}
